// Thread chat surface — the demo's main chat. Wired to the store's
// streaming pipeline (sendMessage → sendToAssistant → SSE → currentMessages).
//
// The store already has the closure-stale fix (uses api.chat()'s return
// value to update messagesByThread), so this page just reads from
// currentMessages + streaming.

import { ArrowUp, Loader2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ActivityTrail } from "@/components/ActivityTrail";
import { ChatBubble } from "@/components/ChatBubble";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

export function ThreadPage() {
  const {
    currentThread,
    currentMessages,
    streaming,
    sendMessage,
    deleteThread,
    route,
    navigate,
  } = useStore();

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const streamingHere = streaming.active && streaming.threadId === currentThread?.id;

  // Pin scroll to bottom as the conversation grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [
    currentMessages.length,
    streaming.partial,
    streaming.activity.length,
    streaming.todos.length,
  ]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streamingHere) return;
    setInput("");
    await sendMessage(text);
  }, [input, streamingHere, sendMessage]);

  if (!route.threadId) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10 py-12">
        <p className="text-sm text-muted-foreground italic">No thread selected.</p>
      </div>
    );
  }

  if (!currentThread) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10 py-12">
        <p className="text-sm text-muted-foreground italic">Loading thread…</p>
      </div>
    );
  }

  const empty = currentMessages.length === 0 && !streamingHere;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Sub-header */}
      <div className="border-b border-border bg-background">
        <div className="mx-auto w-full max-w-3xl px-6 md:px-10 py-5 flex items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              Thread
            </p>
            <h1 className="font-freight text-3xl font-light tracking-tight leading-tight truncate">
              {currentThread.title}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-destructive shrink-0"
            onClick={async () => {
              if (
                confirm(`Delete thread "${currentThread.title}"? This wipes its messages.`)
              ) {
                await deleteThread(currentThread.id);
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-surface-subtle px-6 md:px-10 py-6"
      >
        <div className="mx-auto w-full max-w-3xl space-y-5">
          {empty && (
            <div className="rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
              <p className="text-sm text-muted-foreground italic">
                Ask Inku, or drop a brief.
              </p>
              <p className="mt-2 text-xs text-muted-foreground/70">
                The Creative Director runs the conversation. Tools, todos,
                and pickers stream into the activity trail as they happen.
              </p>
            </div>
          )}

          {currentMessages.map((m) => (
            <div key={m.id} className="space-y-3">
              <ChatBubble role={m.role === "system" ? "assistant" : m.role}>
                {typeof m.content === "string" ? m.content : ""}
              </ChatBubble>
              {m.role === "assistant" && (
                <ActivityTrail
                  activity={m.activity ?? []}
                  todos={m.todos ?? []}
                />
              )}
            </div>
          ))}

          {/* Live streaming row */}
          {streamingHere && (
            <>
              {!streaming.partial &&
                streaming.activity.length === 0 &&
                streaming.todos.length === 0 && (
                  <div className="inline-flex items-center gap-2 rounded-lg bg-card border border-border px-3 py-2 text-sm text-muted-foreground italic">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Inku is thinking…
                  </div>
                )}
              {streaming.partial && (
                <ChatBubble role="assistant" streaming>
                  {streaming.partial}
                </ChatBubble>
              )}
              {(streaming.todos.length > 0 || streaming.activity.length > 0) && (
                <ActivityTrail
                  activity={streaming.activity}
                  todos={streaming.todos}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-background">
        <div className="mx-auto w-full max-w-3xl px-6 md:px-10 py-4">
          <div className="flex items-end gap-3 rounded-xl border border-border bg-card shadow-card focus-within:ring-2 focus-within:ring-ring/30 transition-shadow px-4 py-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                streamingHere ? "Inku is responding…" : "Ask Inku, or drop a brief…"
              }
              disabled={streamingHere}
              rows={1}
              className={cn(
                "flex-1 resize-none bg-transparent outline-none text-sm placeholder:text-muted-foreground/70",
                "min-h-[24px] max-h-32 leading-relaxed"
              )}
            />
            <Button
              onClick={send}
              disabled={streamingHere || !input.trim()}
              size="icon-sm"
              shape="pill"
              className="shrink-0"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
